import h from 'hyperscript';
import { type API as Slider, type Options as SliderOptions, create as createNoUiSlider } from 'nouislider';

import handleIcon from '@disy/cadenza-icons/slider-handle-11x24.svg';
import mobileHandleIcon from '@disy/cadenza-icons/slider-handle-mobile-15x30.svg';
import type { StyleClass } from '@disy/lib/add-style-class';
import { addStyleClass } from '@disy/lib/add-style-class';
import { assert, assertNonNullable } from '@disy/lib/custom-error';
import { debounce } from '@disy/lib/debounce';
import { icon } from '@disy/lib/icon';
import { position } from '@disy/lib/position';
import { uniqueId } from '@disy/lib/unique-id';
import { getPopupParent } from '@disy/ui/popup';
import '@disy/ui/popup.css';

import './slider.css';

const MIN_PIXELS_PER_TICK = 4;
const HOVER_TOOLTIP_HIDE_TIMEOUT = 200;

// Visual offset is needed to expand the width of the steps-slider-thumb because the end date
// of the condition value gets subtracted one unit of the smallest condition date format unit.
// See DateTimeCondition._getAdjustedUpperDate()
const VISUAL_OFFSET = 1;
const DEFAULT_STEP_SLIDER_WIDTH = 240;

type ValueArgument = number | string | null;
type HandleOverlapSliderInteraction = (
  values: ValueArgument[],
  type: 'change' | 'slide',
  slider: Slider,
) => { lowerTickValue: number; upperTickValue: number };
export type SliderFormatFunction = (value: ValueArgument) => string;
interface SliderPopup extends HTMLElement {
  show(): void;
  hide(): void;
  valueEl: HTMLElement;
  updateValue(value: number[]): void;
}

/**
 * Creates an empty placeholder slider that does not have any handles.
 *
 * @param target - The element to attach the slider to.
 * */
export function createEmptySlider(target: HTMLElement) {
  const slider = createSlider(
    target,
    {
      range: { min: 0, max: 100 },
      start: 50, // arbitrary value, the handle is hidden anyway
    },
    { hideHandles: true },
  );
  return slider;
}

/**
 * Creates a (noUi-)slider instance.
 *
 * @param target - The element to attach the slider to.
 * @param sliderConfig - noUiSlider configuration, see https://refreshless.com/nouislider/slider-options/
 * @param customConfig - Custom config options that get not passed to noUiSlider.
 * @param customConfig.isUnset - Whether the slider is unset.
 * @param customConfig.simplePips - Whether to display pips.
 * @param customConfig.steppingMode - Whether to use stepping mode where the thumb snap to predefined values.
 * @param customConfig.handleOverlapSliderInteraction - Required if steppingMode is true: callback to calculate lower and upper value.
 * @param customConfig.formatFunction - Function to format pips.
 * @param customConfig.hideHandles - Whether to hide the slider handles.
 * @return (noUi-)slider-API.
 */
export function createSlider(
  target: HTMLElement,
  sliderConfig: SliderOptions,
  {
    isUnset = false,
    simplePips = false,
    steppingMode = false,
    handleOverlapSliderInteraction,
    formatFunction,
    hideHandles = false,
  }: {
    isUnset?: boolean;
    simplePips?: boolean;
    steppingMode?: boolean;
    handleOverlapSliderInteraction?: HandleOverlapSliderInteraction;
    formatFunction?: SliderFormatFunction;
    hideHandles?: boolean;
  } = {},
) {
  if (steppingMode) {
    // Use only the first value, calculate the other depending on the time window.
    sliderConfig.start = [(sliderConfig.start as ValueArgument[])[0]];
    assert(
      typeof handleOverlapSliderInteraction === 'function',
      'When using steppingMode then handleOverlapSliderInteraction must be passed',
    );
  }
  if (simplePips && sliderConfig.range) {
    const { min, max } = sliderConfig.range as { min: number; max: number };
    const tickCount = (max - min) / sliderConfig.step! + 1;
    const sliderWidth = parseInt(getComputedStyle(target.parentElement!).width) || 264;
    if (tickCount * MIN_PIXELS_PER_TICK <= sliderWidth) {
      const values = Array.from({ length: tickCount }, (v, k) => k);
      sliderConfig.pips = Object.assign(sliderConfig.pips || {}, {
        mode: 'values',
        values,
        stepped: true,
        density: 100 / tickCount,
      }) as SliderOptions['pips'];
    } else {
      // always generate pips at the start and end points to display start/end labels, even if tick count is too large.
      sliderConfig.pips = {
        mode: 'positions', // labels only at the given percentage-based positions
        values: [0, 100], // only at the start and end points
        density: 100, // density 1 pip every 100% -> no pips between start and end
      } as SliderOptions['pips'];
    }
    if (formatFunction) {
      sliderConfig.pips!.format = { to: formatFunction };
    }
  }
  if (sliderConfig.keyboardSupport === undefined) {
    sliderConfig.keyboardSupport = false;
  }

  const stepsOffsetElement = h('.slider--steps-offset');
  if (steppingMode) {
    target.appendChild(stepsOffsetElement);
  }
  const slider = createNoUiSlider(target, sliderConfig);
  slider.target.classList.toggle('fixed-range', steppingMode);

  const handles = slider.target.querySelectorAll('.noUi-handle');
  if (steppingMode) {
    slider.target.classList.add('slider--flex-parent');
    const { min, max } = sliderConfig.range as { min: number[]; max: number[] };
    slider.target.setAttribute('data-slider-min-value', String(min[0]));
    if (min[0] > 0) {
      // When the slider date range start is after the total date range start, the slider
      // has an offset to the left that is not reachable by dragging the slider thumb.
      stepsOffsetElement.style.flex = String(min[0]);
      slider.target.querySelector<HTMLElement>('.noUi-base')!.style.flex = String(max[0] - min[0]);
    }
    setupSteppingMode(slider, handles, sliderConfig, handleOverlapSliderInteraction!);
    slider.target.classList.toggle('range-thumb', true);
  } else {
    setupHandles(handles);
  }

  if (isUnset) {
    setSliderUnset(slider);
  }

  target.classList.toggle('hidden-handles', hideHandles);

  return slider;
}

function setupHandles(handles: NodeListOf<Element>) {
  for (const handle of handles) {
    const mobile = icon(mobileHandleIcon, { styleClass: 'touch-handle-icon', variant: 'normal' });
    const desktop = icon(handleIcon, { styleClass: 'handle-icon', variant: 'normal' });
    handle.append(mobile, desktop);
  }
}

function setupSteppingMode(
  slider: Slider,
  handles: NodeListOf<Element>,
  sliderConfig: SliderOptions,
  handleOverlapSliderInteraction: HandleOverlapSliderInteraction,
) {
  handles.item(0).append(h('.d-slider--fixed-range-handle'));

  const parentWidth = slider.target.getClientRects().item(0)?.width || DEFAULT_STEP_SLIDER_WIDTH;
  const { min, max } = sliderConfig.range as { min: number[]; max: number[] };
  const sliderElementWidth = parentWidth * ((max[0] - min[0]) / max[0]);
  const handle = slider.target.querySelector<HTMLElement>('.noUi-handle-lower')!;
  const sliderRangeMax = sliderConfig.range.max as number; // WTF, how can this be number if it's number[] above?

  const initialOverlap = handleOverlapSliderInteraction([(sliderConfig.start as ValueArgument[])[0]], 'slide', slider);
  updateOverlappingHandle(
    slider,
    sliderRangeMax,
    sliderElementWidth,
    handle,
    initialOverlap.upperTickValue - initialOverlap.lowerTickValue,
    sliderConfig,
  );

  const handleSliderInteractionInternal = (values: ValueArgument[], type: 'change' | 'slide') => {
    const { lowerTickValue, upperTickValue } = handleOverlapSliderInteraction(values, type, slider);
    slider.target.dispatchEvent(
      new CustomEvent(`overlap-slider-${type}`, {
        detail: {
          values: [lowerTickValue, upperTickValue],
        },
      }),
    );
    updateOverlappingHandle(
      slider,
      sliderRangeMax,
      sliderElementWidth,
      handle,
      upperTickValue - lowerTickValue,
      sliderConfig,
    );
  };

  slider.on('change', (values) => {
    handleSliderInteractionInternal(values, 'change');
  });

  slider.on('slide', (values) => {
    handleSliderInteractionInternal(values, 'slide');
  });
}

// Adjust the width of the slider to be proportional with the time window.
function updateOverlappingHandle(
  slider: Slider,
  sliderRangeMax: number,
  sliderElementWidth: number,
  handle: HTMLElement,
  timeWindow: number,
  sliderConfig: SliderOptions,
) {
  const left = (slider.getPositions()[0] * sliderElementWidth) / 100;
  let newHandleWidth =
    ((timeWindow + VISUAL_OFFSET) * sliderElementWidth) / (sliderRangeMax - (sliderConfig.range.min as number[])[0]); // pixel per tick
  if (left + newHandleWidth > sliderElementWidth) {
    newHandleWidth = sliderElementWidth - left;
  }
  handle.style.width = `${newHandleWidth}px`;
  handle.style.right = `-${newHandleWidth}px`;
}

function setSliderUnset(slider: Slider) {
  slider.target.classList.add('unset');
  const removeUnset = () => {
    slider.target.classList.remove('unset');
    // unfortunately slider.off('.unset') does not work because of a bug in noUiSlider.
    slider.off('set.unset');
    slider.off('start.unset');
  };
  slider.on('start.unset', removeUnset);
  slider.on('set.unset', removeUnset);
}

export function createSliderTooltips(
  slider: Slider,
  formatFunction: SliderFormatFunction,
  separator: string,
  styleClass?: StyleClass,
) {
  const initialValues = Array.isArray(slider.options.start) ? slider.options.start : [slider.options.start];
  const tooltips = initialValues.map((tickValue, handle) => {
    const handleEl = slider.target.querySelector(`.noUi-handle[data-handle="${handle}"]`);
    return createTooltipElement(handleEl, (values) => formatFunction(values[handle]), styleClass);
  });

  const connectElement = slider.target.getElementsByClassName('noUi-connect')[0];
  let connectTooltip: SliderPopup | undefined;
  if (connectElement) {
    const connectFormatFunction = (tickValues: ValueArgument[]) =>
      tickValues.map((tickValue) => formatFunction(tickValue)).join(` ${separator} `) || ' ';
    connectTooltip = createTooltipElement(connectElement, connectFormatFunction);
  }
  const tooltipDisplayStarted: boolean[] = Array(initialValues.length).fill(false);
  const KEY_HANDLE = 0;
  let activeTooltip: SliderPopup | undefined;

  const createTooltip = (values: number[], handle: number) => {
    tooltipDisplayStarted[handle] = true;
    setTimeout(() => {
      if (activeTooltip || !tooltipDisplayStarted.some((tooltipStarted) => tooltipStarted)) {
        return;
      }
      if (connectElement && tooltipDisplayStarted.every((tooltipStarted) => tooltipStarted)) {
        activeTooltip = connectTooltip;
      } else {
        activeTooltip = tooltips[handle];
      }

      assertNonNullable(activeTooltip);
      activeTooltip.updateValue(values);
      activeTooltip.show();
    });
  };

  const updateTooltip = (values: number[]) => {
    if (activeTooltip) {
      activeTooltip.updateValue(values);
    }
  };

  const hideTooltip = (handle: number) => {
    tooltipDisplayStarted[handle] = false;
    if (activeTooltip) {
      activeTooltip.hide();
      activeTooltip = undefined;
    }
  };

  slider.on('start', (values, handle) => createTooltip(values as number[], handle));
  slider.on('update', (values) => updateTooltip(values as number[]));
  slider.on('end', (values, handle) => hideTooltip(handle));

  const keyUpDebounce = debounce(() => {
    slider.updateOptions({ step: 1 }, true);
    // The slider stops moving anyway when pressing any key other than arrow keys, so we need to hide
    // the tooltip for any key up, otherwise we will have many remaining tooltips displayed.
    hideTooltip(KEY_HANDLE);
  });
  const sliderHandle = slider.target.querySelector<HTMLElement>('.noUi-handle-lower')!;
  sliderHandle.onkeydown = (event) => {
    // By default, the slider jumps one unit on an arrow key pressed, when the slider handle is focused.
    // The requirement was that the slider jumps over 10 units when the SHIFT key is hold down
    // and the user presses an arrow key.
    // This is why we are setting the step to 10 when detecting the SHIFT key being down
    // and resetting to the default value of 1 on key up.
    if (event.shiftKey && event.key.startsWith('Arrow')) {
      slider.updateOptions({ step: 10 }, true);
    }
    if ((event.shiftKey || event.key.startsWith('Arrow')) && !tooltipDisplayStarted[KEY_HANDLE]) {
      createTooltip([slider.get() as number], KEY_HANDLE);
    }
  };
  sliderHandle.onkeyup = () => keyUpDebounce();
}

/**
 * Display tooltips when hovering the mouse cursor over the slider.
 * Hover tooltip requires 'hover' behaviour in noUiSlider. To make it work add this to the slider configuration:
 *   { behaviour: 'tap-hover' }
 *
 * @param slider - the slider
 * @param formatFunction - the tooltip format function
 * @param styleClass - Style class
 * @param handleOnly - a flag for setting the tooltip be displayed only when hovering the slider handle.
 * If no value set or set to false then tooltips are displayed when hovering any slider area.
 */
export function createSliderHoverTooltip(
  slider: Slider,
  formatFunction: SliderFormatFunction,
  styleClass?: StyleClass,
  handleOnly = false,
) {
  let hideTimeout: number | undefined;

  const line = h('.d-slider--hover-line', { hidden: true });
  slider.target.append(line);
  const tooltip = createTooltipElement(line, (values) => formatFunction(values[0]), styleClass)!;

  const showTooltip = (value: number) => {
    if (getOtherTooltips(tooltip).some((t) => t.hidden === false)) {
      return;
    }
    clearTimeout(hideTimeout);

    line.style.left = `${sliderValueToPercent(slider, value)}%`;
    tooltip.updateValue([value]);

    if (line.hidden || tooltip.hidden !== false) {
      line.hidden = false;
      tooltip.show();
    }
    position(tooltip, tooltip.parentElement!, 'top');
  };

  const hideTooltip = () => {
    line.hidden = true;
    tooltip.hide();
  };

  if (handleOnly) {
    const handle = slider.target.querySelector<HTMLElement>('.noUi-handle-lower')!;
    handle.onmouseenter = () => showTooltip(slider.get() as number);
    handle.onmouseleave = () => hideTooltip();
    handle.onmouseup = () => hideTooltip();
    slider.on('update', () => hideTooltip());
  } else {
    slider.on('hover', (value) =>
      showTooltip(value as unknown as number /* WTF, how can this be a number? Wrong NoUiSlider types? */),
    );

    slider.on('start', () => hideTooltip());
    slider.target.onmouseleave = () => {
      clearTimeout(hideTimeout);
      hideTimeout = setTimeout(hideTooltip, HOVER_TOOLTIP_HIDE_TIMEOUT);
    };
  }
}

function sliderValueToPercent(slider: Slider, value: number) {
  const { min, max } = slider.options.range as { min: number; max: number };
  return Math.round(((value - min) / (max - min)) * 10000) / 100;
}

function createTooltipElement(
  target: Element | null,
  formatFunction: (values: ValueArgument[]) => string,
  styleClass?: StyleClass,
) {
  if (!target) {
    return undefined;
  }

  const sliderTooltip = createSliderPopup();
  addStyleClass(sliderTooltip, styleClass);
  getPopupParent(target).append(sliderTooltip);
  sliderTooltip.updateValue = (values) => {
    sliderTooltip.valueEl.textContent = formatFunction(values.map(Math.round)) || ' ';
    position(sliderTooltip, target, 'top');
  };
  sliderTooltip.show = () => {
    sliderTooltip.hidden = false;
    for (const tooltip of getOtherTooltips(sliderTooltip)) {
      tooltip.hidden = true;
    }
  };
  sliderTooltip.hide = () => (sliderTooltip.hidden = true);
  sliderTooltip.hidden = true;
  return sliderTooltip;
}

function getOtherTooltips(tooltip: SliderPopup) {
  return [...document.querySelectorAll<SliderPopup>(`.d-slider-popup:not([id="${tooltip.id}"])`)];
}

function createSliderPopup() {
  const valueHolder = h('span.d-slider-popup--value');
  // Not using createPopup(), because the listener that are added there would cause flickering and we don't need them.
  const popup = h<SliderPopup>(
    '.d-popup.d-slider-popup',
    {
      id: uniqueId(),
      attrs: { role: 'dialog' },
    },
    valueHolder,
  );
  popup.valueEl = valueHolder;
  return popup;
}
